import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase, supabaseConfigured } from '../lib/supabaseClient';
import { registerBizApiHandlers } from '../services/bizApi';

const BusinessAuthContext = createContext(null);

const ORG_ID_KEY = 'divispend_biz_org_id';

export function BusinessAuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aal, setAal] = useState('aal1');
  const [activeOrgId, setActiveOrgIdState] = useState(() => localStorage.getItem(ORG_ID_KEY) || null);
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;

  const refreshAal = useCallback(async () => {
    if (!supabaseConfigured) return 'aal1';
    const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
    const level = !error && data ? data.currentLevel : 'aal1';
    setAal(level);
    return level;
  }, []);

  useEffect(() => {
    if (!supabaseConfigured) {
      setLoading(false);
      return;
    }
    supabase.auth.getSession().then(async ({ data }) => {
      setSession(data.session);
      if (data.session) await refreshAal();
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange(async (event, sess) => {
      setSession(sess);
      if (sess) await refreshAal();
      else setAal('aal1');
      if (event === 'SIGNED_OUT') setActiveOrgIdState(null);
    });
    return () => sub.subscription.unsubscribe();
  }, [refreshAal]);

  useEffect(() => {
    registerBizApiHandlers({
      sessionExpired: () => {
        toast.error('Your business session expired — please sign in again');
        setSession(null);
        navigate('/biz/login', { state: { from: locationRef.current }, replace: true });
      },
      stepUpRequired: () => {
        navigate(`/biz/mfa/enroll?next=${encodeURIComponent(locationRef.current.pathname)}`, { replace: true });
      },
    });
  }, [navigate]);

  const setActiveOrgId = useCallback((orgId) => {
    if (orgId) localStorage.setItem(ORG_ID_KEY, orgId);
    else localStorage.removeItem(ORG_ID_KEY);
    setActiveOrgIdState(orgId);
  }, []);

  const signInWithGoogle = useCallback(async () => {
    const redirectTo = `${window.location.origin}/biz/auth/callback`;
    return supabase.auth.signInWithOAuth({ provider: 'google', options: { redirectTo } });
  }, []);

  const signInWithOtp = useCallback(async (email) => {
    return supabase.auth.signInWithOtp({
      email,
      options: { emailRedirectTo: `${window.location.origin}/biz/auth/callback` },
    });
  }, []);

  const verifyOtp = useCallback(async (email, token) => {
    return supabase.auth.verifyOtp({ email, token, type: 'email' });
  }, []);

  const listFactors = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.listFactors();
    if (error) throw error;
    return data;
  }, []);

  const enrollTotp = useCallback(async () => {
    const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp' });
    if (error) throw error;
    return data;
  }, []);

  const challenge = useCallback(async (factorId) => {
    const { data, error } = await supabase.auth.mfa.challenge({ factorId });
    if (error) throw error;
    return data;
  }, []);

  const verifyFactor = useCallback(async (factorId, challengeId, code) => {
    const { data, error } = await supabase.auth.mfa.verify({ factorId, challengeId, code });
    if (error) throw error;
    const { data: refreshed } = await supabase.auth.refreshSession();
    setSession(refreshed.session);
    await refreshAal();
    return data;
  }, [refreshAal]);

  const unenrollFactor = useCallback(async (factorId) => {
    const { error } = await supabase.auth.mfa.unenroll({ factorId });
    if (error) throw error;
  }, []);

  const signOutGlobal = useCallback(async () => {
    await supabase.auth.signOut({ scope: 'global' });
    setActiveOrgId(null);
    setSession(null);
    setAal('aal1');
  }, [setActiveOrgId]);

  const getAccessToken = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || null;
  }, []);

  const value = {
    supabaseConfigured,
    session,
    user: session?.user || null,
    loading,
    aal,
    isAal2: aal === 'aal2',
    activeOrgId,
    setActiveOrgId,
    signInWithGoogle,
    signInWithOtp,
    verifyOtp,
    listFactors,
    enrollTotp,
    challenge,
    verifyFactor,
    unenrollFactor,
    signOutGlobal,
    getAccessToken,
    refreshAal,
  };

  return <BusinessAuthContext.Provider value={value}>{children}</BusinessAuthContext.Provider>;
}

export function useBusinessAuth() {
  const ctx = useContext(BusinessAuthContext);
  if (!ctx) throw new Error('useBusinessAuth must be used within a BusinessAuthProvider');
  return ctx;
}
